import React from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface ErrorContext {
  userId?: string | number;
  organizationId?: string | number;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

export interface ErrorHandlerOptions {
  showToast?: boolean;
  toastMessage?: string;
  severity?: 'error' | 'warning' | 'info';
  captureInSentry?: boolean;
  userFacing?: boolean;
}

/**
 * Centralized error handler that replaces console.log/error statements
 * - Captures errors in Sentry for developers
 * - Shows localized toast messages to users
 * - Provides proper error context and metadata
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private translationHook: any = null;

  private constructor() {}

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Initialize the error handler with translation hook
   */
  public initialize(translationHook: any) {
    this.translationHook = translationHook;
  }

  /**
   * Handle errors with proper logging, Sentry capture, and user notification
   */
  public handleError(
    error: Error | string,
    context: ErrorContext = {},
    options: ErrorHandlerOptions = {}
  ): void {
    const {
      showToast = true,
      toastMessage,
      severity = 'error',
      captureInSentry = true,
      userFacing = true,
    } = options;

    // Determine error message
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Capture error in Sentry for developers
    if (captureInSentry) {
      this.captureInSentry(error, context);
    }

    // Show toast notification to users (if user-facing error)
    if (showToast && userFacing) {
      this.showUserNotification(toastMessage || errorMessage, severity);
    }

    // Log additional context for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      this.logForDevelopment(errorMessage, context, errorStack);
    }
  }

  /**
   * Handle debug logging (removed in production)
   */
  public debug(message: string, data?: any): void {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      // Use structured logging instead of console.log
      this.logForDevelopment(`[DEBUG] ${message}`, { debugData: data });
    }
  }

  /**
   * Handle info logging with proper structure
   */
  public info(message: string, context: ErrorContext = {}): void {
    // Use Sentry breadcrumbs for info logging
    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: context,
    });

    if (process.env.NODE_ENV === 'development') {
      this.logForDevelopment(`[INFO] ${message}`, context);
    }
  }

  /**
   * Handle warnings with optional user notification
   */
  public warn(message: string, context: ErrorContext = {}, showToUser = false): void {
    // Add warning to Sentry breadcrumbs
    Sentry.addBreadcrumb({
      message,
      level: 'warning',
      data: context,
    });

    if (showToUser) {
      this.showUserNotification(message, 'warning');
    }

    if (process.env.NODE_ENV === 'development') {
      this.logForDevelopment(`[WARN] ${message}`, context);
    }
  }

  /**
   * Capture error in Sentry with proper context
   */
  private captureInSentry(error: Error | string, context: ErrorContext): void {
    try {
      // Set user context if available
      if (context.userId || context.organizationId) {
        Sentry.setUser({
          id: context.userId?.toString(),
          organization_id: context.organizationId?.toString(),
        });
      }

      // Set tags for better error categorization
      if (context.component) {
        Sentry.setTag('component', context.component);
      }
      if (context.action) {
        Sentry.setTag('action', context.action);
      }

      // Set additional context
      if (context.metadata) {
        Sentry.setContext('error_metadata', context.metadata);
      }

      // Capture the error
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(error, 'error');
      }
    } catch (sentryError) {
      // Fallback: if Sentry fails, at least log in development
      if (process.env.NODE_ENV === 'development') {
        this.logForDevelopment('Sentry capture failed', {
          originalError: error,
          sentryError,
        });
      }
    }
  }

  /**
   * Show localized toast notification to user
   */
  private showUserNotification(message: string, severity: 'error' | 'warning' | 'info'): void {
    try {
      // Smart translation mapping - map technical errors to user-friendly messages
      const localizedMessage = this.getLocalizedErrorMessage(message);

      // Show appropriate toast based on severity
      switch (severity) {
        case 'error':
          toast.error(localizedMessage);
          break;
        case 'warning':
          toast.warning(localizedMessage);
          break;
        case 'info':
          toast.info(localizedMessage);
          break;
      }
    } catch (toastError) {
      // Fallback: if toast fails, at least capture in Sentry
      if (process.env.NODE_ENV === 'development') {
        this.logForDevelopment('Toast notification failed', {
          originalMessage: message,
          toastError,
        });
      }
    }
  }

  /**
   * Get localized error message with smart mapping
   */
  private getLocalizedErrorMessage(message: string): string {
    // Map technical error messages directly to translation keys
    const errorKeyMap: Record<string, string> = {
      // Admin errors
      'Failed to delete organization': 'admin.deleteOrganizationFailed',
      'Failed to export analytics data': 'admin.analyticsExportFailed',
      'Failed to export audit logs': 'admin.auditLogsExportFailed',
      'Failed to export data': 'admin.exportDataFailed',
      'Failed to load organizations': 'admin.loadOrganizationsFailed',
      'Failed to update organization status': 'admin.updateOrganizationStatusFailed',
      // Api errors
      'API request failed': 'api.apirequestFailed',
      'Create API key error': 'api.createapikeyError',
      'Delete API key error': 'api.deleteapikeyError',
      'Failed to create API key': 'api.createapikeyFailed',
      'Failed to delete API key': 'api.deleteapikeyFailed',
      'Failed to fetch API keys': 'api.fetchapikeysFailed',
      'Failed to fetch API usage stats': 'api.fetchapiusagestatsFailed',
      'Failed to toggle API key': 'api.toggleapikeyFailed',
      'Slow API request detected': 'api.slowapirequestdetected',
      'Toggle API key error': 'api.toggleapikeyError',
      'Validation API error': 'api.validationapiError',
      // Assets errors
      'Asset name cannot be empty': 'assets.assetnamecannotbeempty',
      'Assets is not an array in groupAssetsByBaseName':
        'assets.assetsisnotarraygroupassetsbybasename',
      'Error setting primary asset': 'assets.settingprimaryassetError',
      'Failed to delete asset': 'assets.deleteassetFailed',
      'Failed to fetch assets': 'assets.fetchassetsFailed',
      'Failed to read cached assets': 'assets.readcachedassetsFailed',
      'Failed to read cached assets from localStorage': 'assets.readcachedassetslocalstorageFailed',
      'Failed to render AssetsTab': 'assets.renderassetstabFailed',
      'Failed to set asset as primary': 'assets.setassetasprimaryFailed',
      'Failed to upload asset': 'assets.uploadassetFailed',
      'Failed to upload asset file': 'assets.uploadassetfileFailed',
      // AttributeManagement errors
      'Failed to add attribute to product': 'attributeManagement.attributeAdditionFailed',
      'Failed to create new attribute': 'attributeManagement.attributeCreationFailed',
      'Failed to load measurement units': 'attributeManagement.measurementUnitsLoadFailed',
      // Attributes errors
      'API error creating attribute value': 'attributes.apicreatingattributevalueError',
      'Attribute group update failed': 'attributes.attributegroupupdateFailed',
      'Attribute value validation failed': 'attributes.validationFailed',
      'Error getting attribute groups with usage':
        'attributes.gettingattributegroupswithusageError',
      'Error saving attribute': 'attributes.savingattributeError',
      'Failed to add attribute': 'attributes.addattributeFailed',
      'Failed to add attribute group to family': 'attributes.addattributegroupfamilyFailed',
      'Failed to add attribute to group': 'attributes.addattributegroupFailed',
      'Failed to create attribute group': 'attributes.createattributegroupFailed',
      'Failed to create attribute value': 'attributes.createattributevalueFailed',
      'Failed to create attribute value in handleSaveNewValue':
        'attributes.createattributevaluehandlesavenewvalueFailed',
      'Failed to create attribute value in onError': 'attributes.createattributevalueonerrorFailed',
      'Failed to delete attribute group': 'attributes.deleteattributegroupFailed',
      'Failed to delete attribute value': 'attributes.deleteattributevalueFailed',
      'Failed to fetch attribute groups': 'attributes.fetchattributegroupsFailed',
      'Failed to fetch attribute values': 'attributes.fetchattributevaluesFailed',
      'Failed to fetch attributes': 'attributes.fetchattributesFailed',
      'Failed to fetch effective attributes': 'attributes.fetcheffectiveattributesFailed',
      'Failed to fetch locale attributes': 'attributes.fetchlocaleattributesFailed',
      'Failed to load attribute configurations': 'attributes.loadattributeconfigurationsFailed',
      'Failed to load attribute data': 'attributes.loadFailed',
      'Failed to override attribute group': 'attributes.overrideattributegroupFailed',
      'Failed to override attributes': 'attributes.overrideattributesFailed',
      'Failed to parse attribute JSON value': 'attributes.parseattributejsonvalueFailed',
      'Failed to remove attribute from group': 'attributes.removeattributegroupFailed',
      'Failed to remove attribute group from family': 'attributes.removeattributegroupfamilyFailed',
      'Failed to reorder attribute group items': 'attributes.reorderattributegroupitemsFailed',
      'Failed to reorder attribute groups': 'attributes.reorderattributegroupsFailed',
      'Failed to reorder attributes in group': 'attributes.reorderattributesgroupFailed',
      'Failed to save attribute value': 'attributes.saveFailed',
      'Failed to update attribute group': 'attributes.updateattributegroupFailed',
      'Failed to update attribute value': 'attributes.updateFailed',
      'Failed to update attribute value in onError': 'attributes.updateattributevalueonerrorFailed',
      // AttributesManagement errors
      'Failed to delete attribute': 'attributesManagement.attributeDeletionFailed',
      'Failed to submit attribute form': 'attributesManagement.formSubmissionFailed',
      // Auth errors
      'Authentication initialization failed': 'auth.sessionExpired',
      'Auto-login failed after setting password': 'auth.autologinaftersettingpasswordFailed',
      'Could not read error response': 'auth.loginFailed',
      'Empty user profile response': 'auth.sessionExpired',
      'Failed to clear form state on session expiry': 'auth.clearformstateonsessionexpiryFailed',
      'Failed to create Stripe checkout session': 'auth.createstripecheckoutsessionFailed',
      'Failed to fetch user memberships': 'auth.loginFailed',
      'Failed to fetch user profile after login': 'auth.loginFailed',
      'Login failed': 'auth.loginFailed',
      'Login request failed with JSON error response': 'auth.loginFailed',
      'Login request failed with text error response': 'auth.loginFailed',
      'Logout failed': 'auth.logoutFailed',
      'Registration failed': 'auth.loginFailed',
      'You are not authorized to perform this action': 'auth.unauthorized',
      'Your session has expired': 'auth.sessionExpired',
      // Categories errors
      'Failed to bulk assign category': 'categories.bulkassigncategoryFailed',
      'Failed to create category': 'categories.createcategoryFailed',
      'Failed to delete category': 'categories.deletecategoryFailed',
      'Failed to fetch category tree': 'categories.fetchcategorytreeFailed',
      'Failed to move category': 'categories.movecategoryFailed',
      'Failed to update category': 'categories.updatecategoryFailed',
      'Invalid category ID provided': 'categories.invalidcategoryidprovided',
      'Unexpected category tree data format': 'categories.unexpectedcategorytreedataformat',
      // CategoryTreeSelect errors
      'Failed to load category tree': 'categoryTreeSelect.loadFailed',
      'Network error occurred while loading categories': 'categoryTreeSelect.networkError',
      'Server error occurred while fetching categories': 'categoryTreeSelect.serverError',
      // Channels errors
      'Failed to create channel': 'channels.createchannelFailed',
      'Failed to delete channel': 'channels.deletechannelFailed',
      'Failed to fetch channel by code': 'channels.fetchchannelbycodeFailed',
      'Failed to fetch channel ID': 'channels.fetchchannelidFailed',
      'Failed to fetch channels': 'channels.fetchchannelsFailed',
      'Failed to fetch sales channels': 'channels.fetchsaleschannelsFailed',
      'Failed to update channel': 'channels.updatechannelFailed',
      // Comments errors
      'Failed to create comment': 'comments.createcommentFailed',
      'Failed to delete comment': 'comments.deletecommentFailed',
      'Failed to fetch comment': 'comments.fetchcommentFailed',
      'Failed to fetch comments': 'comments.fetchcommentsFailed',
      'Failed to update comment': 'comments.updatecommentFailed',
      // Dashboard errors
      'Dashboard data is temporarily unavailable': 'dashboard.dataUnavailable',
      'Failed to load dashboard data': 'dashboard.loadFailed',
      'Failed to load dashboard statistics': 'dashboard.statsLoadFailed',
      'Failed to load dashboard summary': 'dashboard.summaryLoadFailed',
      'Failed to load incomplete products': 'dashboard.incompleteProductsLoadFailed',
      'Failed to load recent activity': 'dashboard.activityLoadFailed',
      'Failed to refresh dashboard data': 'dashboard.refreshFailed',
      // Exports errors
      'Export endpoint test failed': 'exports.exportendpointtestFailed',
      'Failed to export organization data': 'exports.exportorganizationdataFailed',
      'Failed to export team to CSV': 'exports.exportteamcsvFailed',
      'Unknown report type for export': 'exports.unknownreporttypeexport',
      // Families errors
      'Error getting families with usage': 'families.gettingfamilieswithusageError',
      'Failed to create family': 'families.createfamilyFailed',
      'Failed to delete family': 'families.deletefamilyFailed',
      'Failed to fetch families': 'families.fetchfamiliesFailed',
      'Failed to fetch family by ID': 'families.fetchfamilybyidFailed',
      'Failed to load /api/families/stats/': 'families.loadapifamiliesstatsFailed',
      'Failed to load family completeness data': 'families.loadfamilycompletenessdataFailed',
      'Failed to override family groups': 'families.overridefamilygroupsFailed',
      'Failed to update family': 'families.updatefamilyFailed',
      'Unexpected families data format': 'families.unexpectedfamiliesdataformat',
      // FamilyAttributes errors
      'Failed to load family attribute groups': 'familyAttributes.groupsLoadFailed',
      'Failed to load family attributes': 'familyAttributes.attributesLoadFailed',
      // FeedbackModal errors
      'Failed to submit feedback': 'feedbackModal.submissionFailed',
      'Network error during feedback submission': 'feedbackModal.networkError',
      // Forms errors
      'Contact form validation failed': 'forms.contactFormValidationFailed',
      // General errors
      'Admin status update failed, trying alternative':
        'general.adminstatusupdatefailedtryingalternative',
      'An unexpected error occurred': 'general.unexpectedError',
      'Both bulk and individual archive updates failed':
        'general.bothbulkandindividualarchiveupdatesFailed',
      'Container element not found for table of contents':
        'general.containerelementnotfoundtableofcontents',
      'Debug failed': 'general.debugFailed',
      'Error formatting currency': 'general.formattingcurrencyError',
      'Error loading infographic steps': 'general.loadinginfographicstepsError',
      'Error processing crop': 'general.processingcropError',
      'Error removing avatar': 'general.removingavatarError',
      'Error setting up account': 'general.settingupaccountError',
      'Failed to ${action} job': 'general.actionjobFailed',
      'Failed to bulk ${isActive ? ': 'general.bulkisactiveFailed',
      'Failed to cancel invite': 'general.cancelinviteFailed',
      'Failed to capture error in Sentry': 'general.captureerrorsentryFailed',
      'Failed to check SKU duplicates': 'general.checkskuduplicatesFailed',
      'Failed to cleanup expired state': 'general.cleanupexpiredstateFailed',
      'Failed to clear form states': 'general.clearformstatesFailed',
      'Failed to create blob from canvas': 'general.createblobcanvasFailed',
      'Failed to create connector configuration': 'general.createconnectorconfigurationFailed',
      'Failed to create platform configuration': 'general.createplatformconfigurationFailed',
      'Failed to delete connector configuration': 'general.deleteconnectorconfigurationFailed',
      'Failed to delete platform configuration': 'general.deleteplatformconfigurationFailed',
      'Failed to download template': 'general.downloadtemplateFailed',
      'Failed to fetch analytics for config ${config.id}':
        'general.fetchanalyticsconfigconfigidFailed',
      'Failed to fetch analytics refresh status': 'general.fetchanalyticsrefreshstatusFailed',
      'Failed to fetch analytics summary': 'general.fetchanalyticssummaryFailed',
      'Failed to fetch audit logs': 'general.fetchauditlogsFailed',
      'Failed to fetch available platforms': 'general.fetchavailableplatformsFailed',
      'Failed to fetch background jobs': 'general.fetchbackgroundjobsFailed',
      'Failed to fetch categories': 'general.fetchcategoriesFailed',
      'Failed to fetch connector configurations': 'general.fetchconnectorconfigurationsFailed',
      'Failed to fetch currencies': 'general.fetchcurrenciesFailed',
      'Failed to fetch explicit relations': 'general.fetchexplicitrelationsFailed',
      'Failed to fetch logs for config ${config.id}': 'general.fetchlogsconfigconfigidFailed',
      'Failed to fetch roles': 'general.fetchrolesFailed',
      'Failed to fetch service statuses': 'general.fetchservicestatusesFailed',
      'Failed to fetch system metrics': 'general.fetchsystemmetricsFailed',
      'Failed to format activity date': 'general.formatactivitydateFailed',
      'Failed to get feedback history': 'general.getfeedbackhistoryFailed',
      'Failed to get relative time for date': 'general.getrelativetimedateFailed',
      'Failed to load analytics refresh status': 'general.loadanalyticsrefreshstatusFailed',
      'Failed to load benefits features': 'general.loadbenefitsfeaturesFailed',
      'Failed to load bullets for feature ${feature}': 'general.loadbulletsfeaturefeatureFailed',
      'Failed to load company size options': 'general.loadcompanysizeoptionsFailed',
      'Failed to load comparison table rows': 'general.loadcomparisontablerowsFailed',
      'Failed to load completeness trend': 'general.loadcompletenesstrendFailed',
      'Failed to load FAQ items': 'general.loadfaqitemsFailed',
      'Failed to load features for tier ${tier}': 'general.loadfeaturestiertierFailed',
      'Failed to load initial categories': 'general.loadinitialcategoriesFailed',
      'Failed to load integration platforms': 'general.loadintegrationplatformsFailed',
      'Failed to load key features': 'general.loadkeyfeaturesFailed',
      'Failed to load most missing fields data': 'general.loadmostmissingfieldsdataFailed',
      'Failed to load preferred time options': 'general.loadpreferredtimeoptionsFailed',
      'Failed to load purpose options': 'general.loadpurposeoptionsFailed',
      'Failed to load real features for tier ${tier}': 'general.loadrealfeaturestiertierFailed',
      'Failed to load recent activity from analytics': 'general.loadrecentactivityanalyticsFailed',
      'Failed to load resources': 'general.loadresourcesFailed',
      'Failed to parse CSV headers': 'general.parsecsvheadersFailed',
      'Failed to persist state': 'general.persiststateFailed',
      'Failed to preview mapping': 'general.previewmappingFailed',
      'Failed to process pending invitation': 'general.unexpectedError',
      'Failed to remove persisted state': 'general.removepersistedstateFailed',
      'Failed to resend invite': 'general.resendinviteFailed',
      'Failed to retrieve persisted state': 'general.retrievepersistedstateFailed',
      'Failed to search categories': 'general.searchcategoriesFailed',
      'Failed to submit contact form': 'general.submitcontactformFailed',
      'Failed to update connector configuration': 'general.updateconnectorconfigurationFailed',
      'Failed to update platform configuration': 'general.updateplatformconfigurationFailed',
      'Failed to update platform status': 'general.updateplatformstatusFailed',
      'Failed to update relationship': 'general.updaterelationshipFailed',
      'HTML response detected instead of JSON data':
        'general.htmlresponsedetectedinsteadofjsondata',
      'Image failed to load': 'general.imageloadFailed',
      'Invalid platform selected': 'general.invalidplatformselected',
      'Media upload error': 'general.mediauploadError',
      'Missing image reference or invalid crop dimensions':
        'general.missingimagereferenceorinvalidcropdimensions',
      'No avatar URL returned from server': 'general.noavatarurlreturnedserver',
      'Operation failed': 'general.operationFailed',
      'Password reset confirmation failed': 'general.passwordresetconfirmationFailed',
      'Password reset request failed': 'general.passwordresetrequestFailed',
      'Registration error': 'general.registrationError',
      'Registration error details': 'general.registrationdetailsError',
      'Requested data not found': 'general.dataNotFound',
      'Server response error': 'general.serverresponseError',
      'Silent token refresh failed': 'general.silenttokenrefreshFailed',
      'Unexpected categories data format': 'general.unexpectedcategoriesdataformat',
      'Unknown page format in infinite query': 'general.unknownpageformatinfinitequery',
      // Imports errors
      'Error parsing CSV file': 'imports.csvParsingError',
      'Error parsing Excel file': 'imports.excelParsingError',
      'Failed to parse file': 'imports.fileParsingFailed',
      'Import failed': 'imports.importFailed',
      // LanguageToggle errors
      'Failed to change language': 'languageToggle.changeFailed',
      'Localization error': 'languageToggle.localizationError',
      // Locales errors
      'Failed to create locale': 'locales.createlocaleFailed',
      'Failed to delete locale': 'locales.deletelocaleFailed',
      'Failed to fetch locale by code': 'locales.fetchlocalebycodeFailed',
      'Failed to fetch locale ID': 'locales.fetchlocaleidFailed',
      'Failed to fetch locales': 'locales.fetchlocalesFailed',
      'Failed to update locale': 'locales.updatelocaleFailed',
      'Unexpected locales data format': 'locales.unexpectedlocalesdataformat',
      // Network errors
      'Connection error occurred': 'network.connectionError',
      'Connection test failed': 'network.connectiontestFailed',
      'Invalid request': 'network.badRequest',
      'Request timed out': 'network.timeoutError',
      'Server error occurred': 'network.serverError',
      // OrganizationRequest errors
      'Form validation failed': 'organizationRequest.validationFailed',
      // OrganizationSettings errors
      'Failed to save organization settings': 'organizationSettings.saveFailed',
      'Failed to update organization settings': 'organizationSettings.updateFailed',
      // Organizations errors
      'Admin organization delete failed': 'organizations.adminorganizationdeleteFailed',
      'Error fetching organization': 'organizations.fetchingorganizationError',
      'Failed to fetch organization analytics': 'organizations.fetchorganizationanalyticsFailed',
      'Failed to fetch organization data': 'organizations.fetchorganizationdataFailed',
      'Failed to fetch organization defaults': 'organizations.fetchorganizationdefaultsFailed',
      'Failed to fetch organizations from admin endpoint':
        'organizations.fetchorganizationsadminendpointFailed',
      'Failed to update organization': 'organizations.updateorganizationFailed',
      'Fallback organization fetch failed': 'organizations.fallbackorganizationfetchFailed',
      'Fallback organization status update failed':
        'organizations.fallbackorganizationstatusupdateFailed',
      // PriceSummaryBadge errors
      'Failed to fetch price summary': 'priceSummaryBadge.fetchFailed',
      // PriceTab errors
      'Failed to delete price': 'priceTab.deleteFailed',
      'Failed to load price data': 'priceTab.loadFailed',
      'Failed to update price': 'priceTab.updateFailed',
      // Prices errors
      'Error adding price': 'prices.addingpriceError',
      'Error deleting price': 'prices.deletingpriceError',
      'Error fetching prices': 'prices.fetchingpricesError',
      'Error refreshing prices': 'prices.refreshingpricesError',
      'Error refreshing prices after delete': 'prices.refreshingpricesafterdeleteError',
      'Error refreshing prices after update': 'prices.refreshingpricesafterupdateError',
      'Error removing price': 'prices.removingpriceError',
      'Error updating price': 'prices.updatingpriceError',
      'Failed to fetch price history': 'prices.fetchpricehistoryFailed',
      'Failed to fetch price types': 'prices.fetchpricetypesFailed',
      'Failed to fetch prices': 'prices.fetchpricesFailed',
      'Failed to refresh after price update': 'prices.refreshafterpriceupdateFailed',
      'Invalid response format for price history': 'prices.invalidresponseformatpricehistory',
      'Received HTML response instead of JSON when fetching price history':
        'prices.receivedhtmlresponseinsteadofjsonwhenfetchingpricehistory',
      // PricingForm errors
      'Failed to submit pricing form': 'pricingForm.submitFailed',
      // ProductAssets errors
      'Failed to delete product asset': 'productAssets.deleteFailed',
      'Failed to set primary asset': 'productAssets.setPrimaryFailed',
      'Failed to upload product asset': 'productAssets.uploadFailed',
      // ProductGrid errors
      'Error processing product grid data': 'productGrid.dataProcessingError',
      'Failed to load product grid': 'productGrid.loadFailed',
      'Unexpected data structure in product grid': 'productGrid.unexpectedPageStructure',
      // ProductTags errors
      'Failed to create tag': 'productTags.createFailed',
      'Failed to update product tags': 'productTags.updateFailed',
      // Products errors
      'API error in getProductHistory': 'products.apigetproducthistoryError',
      'Error adding tags to products': 'products.addingtagstoproductsError',
      'Error fetching product': 'products.fetchingproductError',
      'Error fetching products': 'products.fetchingproductsError',
      'Error refetching product after setting primary asset':
        'products.refetchingproductaftersettingprimaryassetError',
      'Error removing tags from products': 'products.removingtagsproductsError',
      'Failed to add individual related product': 'products.addindividualrelatedproductFailed',
      'Failed to add multiple products': 'products.addmultipleproductsFailed',
      'Failed to add multiple related products': 'products.addmultiplerelatedproductsFailed',
      'Failed to add product price': 'products.addproductpriceFailed',
      'Failed to add related product': 'products.addRelatedFailed',
      'Failed to add suggested product': 'products.addsuggestedproductFailed',
      'Failed to archive product': 'products.archiveproductFailed',
      'Failed to bulk archive products': 'products.bulkarchiveproductsFailed',
      'Failed to bulk delete products': 'products.bulkdeleteproductsFailed',
      'Failed to bulk update product status': 'products.bulkupdateproductstatusFailed',
      'Failed to create product': 'products.createFailed',
      'Failed to create product attribute': 'products.createproductattributeFailed',
      'Failed to delete product': 'products.deleteFailed',
      'Failed to delete product price': 'products.deleteproductpriceFailed',
      'Failed to download product PDF': 'products.downloadproductpdfFailed',
      'Failed to export products': 'products.exportproductsFailed',
      'Failed to fetch all products': 'products.fetchallproductsFailed',
      'Failed to fetch family details for product': 'products.fetchfamilydetailsproductFailed',
      'Failed to fetch full product data': 'products.fetchfullproductdataFailed',
      'Failed to fetch platform products': 'products.fetchplatformproductsFailed',
      'Failed to fetch prices for product': 'products.fetchpricesproductFailed',
      'Failed to fetch product': 'products.fetchproductFailed',
      'Failed to fetch product assets': 'products.fetchproductassetsFailed',
      'Failed to fetch product assets from API': 'products.fetchproductassetsapiFailed',
      'Failed to fetch product assets on demand': 'products.fetchproductassetsondemandFailed',
      'Failed to fetch product attribute groups on demand':
        'products.fetchproductattributegroupsondemandFailed',
      'Failed to fetch product attributes': 'products.fetchproductattributesFailed',
      'Failed to fetch product completeness': 'products.fetchproductcompletenessFailed',
      'Failed to fetch product completeness data': 'products.fetchproductcompletenessdataFailed',
      'Failed to fetch product history': 'products.fetchproducthistoryFailed',
      'Failed to fetch product versions': 'products.fetchproductversionsFailed',
      'Failed to fetch products': 'products.fetchproductsFailed',
      'Failed to fetch products list': 'products.fetchproductslistFailed',
      'Failed to fetch related products': 'products.fetchrelatedproductsFailed',
      'Failed to fetch user product action': 'products.fetchuserproductactionFailed',
      'Failed to fetch user product actions': 'products.fetchuserproductactionsFailed',
      'Failed to get product suggestions': 'products.getproductsuggestionsFailed',
      'Failed to get product sync status': 'products.getproductsyncstatusFailed',
      'Failed to load assets for product': 'products.loadassetsproductFailed',
      'Failed to load image for related product': 'products.loadimagerelatedproductFailed',
      'Failed to load incomplete products from analytics':
        'products.loadincompleteproductsanalyticsFailed',
      'Failed to load product history': 'products.loadFailed',
      'Failed to load product suggestions': 'products.loadSuggestionsFailed',
      'Failed to load products': 'products.loadFailed',
      'Failed to load recent products from analytics': 'products.loadrecentproductsanalyticsFailed',
      'Failed to refresh product data': 'products.refreshproductdataFailed',
      'Failed to remove attribute value from product': 'products.removeattributevalueproductFailed',
      'Failed to remove related product': 'products.removerelatedproductFailed',
      'Failed to reset filters': 'products.loadFailed',
      'Failed to save product': 'products.saveFailed',
      'Failed to search products': 'products.searchFailed',
      'Failed to sync all products': 'products.syncallproductsFailed',
      'Failed to sync product': 'products.syncproductFailed',
      'Failed to sync product to platforms': 'products.syncproductplatformsFailed',
      'Failed to update product': 'products.updateFailed',
      'Failed to update product attribute': 'products.updateproductattributeFailed',
      'Failed to update product category': 'products.updateproductcategoryFailed',
      'Failed to update product category via bulk_update':
        'products.updateproductcategoryviabulkupdateFailed',
      'Failed to update product description': 'products.updateproductdescriptionFailed',
      'Failed to update product in cache': 'products.updateproductcacheFailed',
      'Failed to update product price': 'products.updateproductpriceFailed',
      'Failed to update related product': 'products.updaterelatedproductFailed',
      'Failed to upload image for new product': 'products.uploadimagenewproductFailed',
      'Failed to upload product image': 'products.uploadproductimageFailed',
      'Invalid organization ID for product completeness':
        'products.invalidorganizationidproductcompleteness',
      'Invalid product IDs for removing relation': 'products.invalidproductidsremovingrelation',
      'Invalid response format for product versions':
        'products.invalidresponseformatproductversions',
      'Invalid response format for related products':
        'products.invalidresponseformatrelatedproducts',
      'Product ID is missing for asset rename': 'products.productidismissingassetrename',
      'Received HTML response instead of JSON in getProductAssets':
        'products.receivedhtmlresponseinsteadofjsongetproductassets',
      'Received HTML response instead of JSON in getProductAttributes':
        'products.receivedhtmlresponseinsteadofjsongetproductattributes',
      'Received HTML response instead of JSON in getProducts':
        'products.receivedhtmlresponseinsteadofjsongetproducts',
      'Received HTML response instead of JSON in getProductVersions':
        'products.receivedhtmlresponseinsteadofjsongetproductversions',
      'Request error in ProductsApi': 'products.requestproductsapiError',
      'Server error in getProductVersions': 'products.servergetproductversionsError',
      'Unexpected response format from products URL':
        'products.unexpectedresponseformatproductsurl',
      'Unexpected response format in getProductAssets':
        'products.unexpectedresponseformatgetproductassets',
      'Unexpected response format in getProductAttributes':
        'products.unexpectedresponseformatgetproductattributes',
      // QueryClient errors
      'Failed to fetch data': 'queryClient.fetchFailed',
      // QueryInvalidation errors
      'Failed to clear local storage cache': 'queryInvalidation.localStorageClearFailed',
      'Failed to invalidate cache': 'queryInvalidation.cacheInvalidationFailed',
      // Reports errors
      'Failed to export report': 'reports.exportFailed',
      'Failed to fetch change history report': 'reports.fetchchangehistoryreportFailed',
      'Failed to fetch completeness report': 'reports.fetchcompletenessreportFailed',
      'Failed to fetch enrichment velocity report': 'reports.fetchenrichmentvelocityreportFailed',
      'Failed to fetch filtered completeness report':
        'reports.fetchfilteredcompletenessreportFailed',
      'Failed to fetch localization quality report': 'reports.fetchlocalizationqualityreportFailed',
      'Failed to fetch readiness report': 'reports.fetchreadinessreportFailed',
      'Failed to initiate report export': 'reports.exportInitiationFailed',
      'Unknown report type': 'reports.unknownReportType',
      // Settings errors
      'Failed to change password': 'settings.passwordChangeFailed',
      'Failed to update preferences': 'settings.preferencesUpdateFailed',
      'Failed to update profile': 'settings.profileUpdateFailed',
      // StatusToggle errors
      'Failed to update product status': 'statusToggle.productStatusUpdateFailed',
      'Failed to update status': 'statusToggle.updateFailed',
      // Sync errors
      'Failed to fetch sync logs': 'sync.fetchsynclogsFailed',
      'Failed to start bulk sync': 'sync.startbulksyncFailed',
      'Failed to update auto sync setting': 'sync.updateautosyncsettingFailed',
      // System errors
      'Failed to parse column order from localStorage': 'system.parsecolumnorderlocalstorageFailed',
      // Tags errors
      'Error creating tag': 'tags.creatingtagError',
      'Error loading all tags': 'tags.loadingalltagsError',
      'Error loading tag options': 'tags.loadingtagoptionsError',
      'Failed to bulk add tags': 'tags.bulkaddtagsFailed',
      'Failed to bulk remove tags': 'tags.bulkremovetagsFailed',
      'Failed to load tags': 'tags.loadFailed',
      'Failed to search tags': 'tags.searchFailed',
      'Failed to update asset tags': 'tags.updateassettagsFailed',
      'Missing required parameters for updateAssetTags':
        'tags.missingrequiredparametersupdateassettags',
      'Received empty response from server when updating asset tags':
        'tags.receivedemptyresponseserverwhenupdatingassettags',
      // Team errors
      'Failed to bulk invite members': 'team.bulkinvitemembersFailed',
      'Failed to fetch team members': 'team.fetchteammembersFailed',
      'Failed to fetch user memberships during initialization':
        'team.fetchusermembershipsduringinitializationFailed',
      'Failed to format member activity timestamp': 'team.formatmemberactivitytimestampFailed',
      'Failed to invite member': 'team.invitememberFailed',
      'Failed to invite user': 'team.userInvitationFailed',
      'Failed to load roles': 'team.loadRolesFailed',
      'Failed to load team members': 'team.loadMembersFailed',
      'Failed to remove member': 'team.removememberFailed',
      'Failed to remove team member': 'team.memberRemovalFailed',
      'Failed to resend invitation': 'team.invitationResendFailed',
      'Failed to send bulk invitations': 'team.bulkInvitationFailed',
      'Failed to update member role': 'team.updatememberroleFailed',
      // Users errors
      'Error checking user existence': 'users.checkinguserexistenceError',
      'Failed to change user password': 'users.changeuserpasswordFailed',
      'Failed to create user': 'users.createuserFailed',
      'Failed to delete user': 'users.deleteuserFailed',
      'Failed to fetch user last action': 'users.fetchuserlastactionFailed',
      'Failed to fetch users': 'users.fetchusersFailed',
      'Failed to load user preferences': 'users.loaduserpreferencesFailed',
      'Failed to save user preferences': 'users.saveuserpreferencesFailed',
      'Failed to update user profile': 'users.updateuserprofileFailed',
      'Failed to update user status': 'users.updateuserstatusFailed',
      // Validation errors
      'Failed to load validation schema': 'validation.schemaLoadFailed',
      'Header validation failed': 'validation.headervalidationFailed',
      'Name validation error': 'validation.namevalidationError',
      'SKU validation error': 'validation.skuvalidationError',
      'SKU validation failed': 'validation.skuValidationFailed',
      'Uniqueness check failed': 'validation.uniquenessCheckFailed',
    };

    // Try to get translation key
    const translationKey = errorKeyMap[message];

    if (translationKey && this.translationHook) {
      try {
        const { t } = this.translationHook;
        const translated = t(translationKey);
        // Only return translated if it's different from the key (meaning translation was found)
        if (translated && translated !== translationKey) {
          return translated;
        }
      } catch {
        // Ignore translation errors
      }
    }

    // If no translation found, return the original message or a generic one
    return message || 'An unexpected error occurred';
  }

  /**
   * Get translation key for common error messages
   */
  private getTranslationKey(message: string): string | null {
    const errorMappings: Record<string, string> = {
      'Failed to refresh dashboard data': 'errors:dashboard.refreshFailed',
      'Unable to load dashboard data': 'errors:dashboard.loadFailed',
      'Failed to load dashboard summary': 'errors:dashboard.summaryLoadFailed',
      'Failed to load recent activity': 'errors:dashboard.activityLoadFailed',
      'Failed to load incomplete products': 'errors:dashboard.incompleteProductsLoadFailed',
      'Connection error occurred': 'errors:network.connectionError',
      'Request timed out': 'errors:network.timeoutError',
      'Server error occurred': 'errors:network.serverError',
      'An unexpected error occurred': 'errors:general.unexpectedError',
    };

    return errorMappings[message] || null;
  }

  /**
   * Structured logging for development
   */
  private logForDevelopment(message: string, context?: any, stack?: string): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      message,
      context,
      stack,
      component: context?.component,
      action: context?.action,
    };

    // Use a single structured log entry instead of multiple console statements
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.group(`KernLogic Error Log: ${message}`);
      // eslint-disable-next-line no-console
      console.table(logEntry);
      if (stack) {
        // eslint-disable-next-line no-console
        console.log('Stack trace:', stack);
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Convenience functions for common use cases
export const logError = (
  error: Error | string,
  context?: ErrorContext,
  options?: ErrorHandlerOptions
) => errorHandler.handleError(error, context, options);

export const logDebug = (message: string, data?: any) => errorHandler.debug(message, data);

export const logInfo = (message: string, context?: ErrorContext) =>
  errorHandler.info(message, context);

export const logWarn = (message: string, context?: ErrorContext, showToUser?: boolean) =>
  errorHandler.warn(message, context, showToUser);

// Hook for React components to use error handler with translations
export const useErrorHandler = () => {
  const translationHook = useTranslation();

  // Initialize error handler with translation hook
  React.useEffect(() => {
    errorHandler.initialize(translationHook);
  }, [translationHook]);

  return {
    handleError: (error: Error | string, context?: ErrorContext, options?: ErrorHandlerOptions) =>
      errorHandler.handleError(error, context, options),
    logError,
    logDebug,
    logInfo,
    logWarn,
  };
};
